import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, Shirt, Users, Swords, ArrowRight, Play } from "lucide-react";
import ToppsCard from "./ToppsCard";
import Lasers from "./Lasers";
import { scrollToSection } from "@/lib/api";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const FadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function Hero({ player, career, pitching }) {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, 200]);
  const ySwing = useTransform(scrollY, [0, 900], [0, 140]);

  return (
    <section
      id="mound"
      data-testid="section-mound"
      className="relative flex min-h-screen items-center overflow-hidden pb-20 pt-28"
    >
      <motion.img
        src="/images/swing-cutout.webp"
        alt=""
        style={{ y: ySwing }}
        className="pointer-events-none absolute -right-[12%] bottom-0 hidden h-[92%] select-none opacity-35 brightness-[0.55] saturate-150 [mask-image:linear-gradient(to_top,transparent_0%,black_40%)] lg:block"
      />
      <Lasers className="opacity-60" />

      <motion.div
        style={{ y: yBg }}
        aria-hidden
        className="pointer-events-none absolute inset-0 flex select-none flex-col justify-center"
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="whitespace-nowrap font-bebas text-[26vw] leading-[0.8] text-outline-white"
          >
            BIG CAL #50 · BIG CAL #50 ·
          </div>
        ))}
      </motion.div>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-[1.15fr_0.85fr]">
        <div className="relative">
          <motion.img
            src="/images/logo-cutout.webp"
            alt="Shoreline All-Stars logo"
            data-testid="hero-logo-badge"
            initial={{ opacity: 0, scale: 0.6, rotate: -16 }}
            animate={{ opacity: 1, scale: 1, rotate: -8 }}
            whileHover={{ rotate: 0, scale: 1.06 }}
            transition={{ delay: 0.85, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="absolute right-0 top-6 w-24 cursor-pointer drop-shadow-[0_10px_30px_rgba(255,46,147,0.4)] sm:w-28 lg:-right-4 lg:top-10 lg:w-40"
          />
          <FadeIn delay={0.15}>
            <div className="mb-8 flex items-center gap-3 font-jbmono text-[11px] uppercase tracking-[0.35em] text-neon">
              <span className="inline-block h-px w-10 bg-neon" />
              The Mound — Player Profile
            </div>
          </FadeIn>

          <h1 className="leading-[0.82] tracking-tight">
            <MaskedLine delay={0.1}>
              <span className="font-bebas text-[clamp(5.5rem,15vw,12rem)]">
                BIG
              </span>
            </MaskedLine>
            <MaskedLine delay={0.24}>
              <span className="font-spray text-[clamp(4.2rem,12vw,10rem)] text-neon drop-shadow-[0_0_35px_rgba(57,255,20,0.45)]">
                CAL
              </span>
            </MaskedLine>
          </h1>

          <MaskedLine delay={0.45} className="mt-8 max-w-md">
            <p data-testid="hero-bio" className="text-base text-white/70">
              {player.bio}
            </p>
          </MaskedLine>

          <FadeIn delay={0.6}>
            <div data-testid="hero-meta" className="mt-8 flex flex-wrap gap-3">
              <span className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-jbmono text-[11px] tracking-[0.15em] text-white/80">
                <Users className="h-3.5 w-3.5 text-neon" />
                <span data-testid="hero-team">{player.team}</span>
              </span>
              <span className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-jbmono text-[11px] tracking-[0.15em] text-white/80">
                <Shirt className="h-3.5 w-3.5 text-hotpink" />
                <span data-testid="hero-jersey">#{player.jersey}</span>
              </span>
              <span className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-jbmono text-[11px] tracking-[0.15em] text-white/80">
                <Swords className="h-3.5 w-3.5 text-neonpurple" />
                <span data-testid="hero-positions">
                  {player.positions.join(" / ")} · BATS {player.bats} · THROWS{" "}
                  {player.throws}
                </span>
              </span>
            </div>
          </FadeIn>

          <FadeIn delay={0.75} className="mt-10 flex flex-wrap gap-4">
            <button
              data-testid="hero-cta-scoreboard"
              onClick={() => scrollToSection("#scoreboard")}
              className="group flex items-center gap-3 rounded-full bg-neon px-8 py-4 font-bebas text-xl tracking-widest text-black transition-shadow duration-300 hover:shadow-[0_0_30px_rgba(57,255,20,0.5)]"
            >
              SEE THE SCOREBOARD
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-highlights"
              onClick={() => scrollToSection("#highlights")}
              className="group flex items-center gap-3 rounded-full border border-white/20 px-8 py-4 font-bebas text-xl tracking-widest text-white transition-colors duration-300 hover:border-hotpink hover:text-hotpink"
            >
              <Play className="h-5 w-5" />
              WATCH HIGHLIGHTS
            </button>
          </FadeIn>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 60, rotate: 5 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ delay: 0.4, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="flex justify-center lg:justify-end"
        >
          <ToppsCard player={player} career={career} pitching={pitching} />
        </motion.div>
      </div>

      <FadeIn
        delay={1.1}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2"
      >
        <div className="flex flex-col items-center gap-2 text-white/40">
          <span className="font-jbmono text-[9px] uppercase tracking-[0.4em]">
            Scroll
          </span>
          <ChevronDown className="h-4 w-4 animate-bounce" />
        </div>
      </FadeIn>
    </section>
  );
}
